import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  ChevronUpIcon,
  ChevronDownIcon,
} from 'lucide-angular';
import type { Producto } from '../../../types/product.types';

export interface PriceRange {
  min: number;
  max: number | null;
  label: string;
  count: number;
  selected: boolean;
}

@Component({
  selector: 'allas-price-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  template: `
    <div class="border-b border-gray-100 pb-4">
      <!-- Header -->
      <div
        class="flex items-center justify-between cursor-pointer border-b-[0.5px] border-gray-700 py-3"
        (click)="toggleExpanded()"
      >
        <span class="font-extrabold text-gray-900 fs-condensed">Precio</span>
        <lucide-icon
          [name]="expanded() ? chevronUpIcon : chevronDownIcon"
          class="w-4 h-4 text-gray-500"
        ></lucide-icon>
      </div>

      <!-- Content -->
      @if (expanded()) {
      <div class="mt-3 space-y-3">
        <!-- Custom Price Range Inputs -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 fs-condensed">
            Establecer rango personalizado:
          </label>
          <div class="flex items-center space-x-2">
            <input
              type="number"
              [placeholder]="'$0'"
              [value]="customMinPrice()"
              (input)="onCustomMinChange($event)"
              (blur)="onCustomMinBlur()"
              class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent fs-condensed"
              min="0"
              step="0.01"
            />
            <span class="text-sm text-gray-500 fs-condensed">a</span>
            <input
              type="number"
              [placeholder]="'Máx'"
              [value]="customMaxPrice()"
              (input)="onCustomMaxChange($event)"
              (blur)="onCustomMaxBlur()"
              class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent fs-condensed"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <!-- Predefined Price Ranges -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 fs-condensed">
            Rangos de precios:
          </label>
          @for (range of visiblePriceRanges(); track range.label) {
          <label class="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              [checked]="range.selected"
              (change)="togglePriceRange(range)"
              class="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500 fs-condensed"
            />
            <span class="text-sm text-gray-700 fs-condensed">{{
              range.label
            }}</span>
            <span class="text-xs text-gray-500">({{ range.count }})</span>
          </label>
          }
        </div>

        <!-- Show More/Less Button -->
        @if (priceRanges().length > 4) {
        <button
          (click)="toggleShowAllPriceRanges()"
          class="text-xs text-blue-600 hover:text-blue-800 underline mt-2 fs-condensed"
        >
          {{ showAllPriceRanges() ? 'Ver menos' : 'Ver más' }}
        </button>
        }

        <!-- Clear Price Filters -->
        @if (hasActivePriceFilters()) {
        <button
          (click)="clearPriceFilters()"
          class="w-full text-sm text-gray-600 hover:text-gray-800 underline fs-condensed"
        >
          Limpiar filtros de precio
        </button>
        }
      </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class PriceFilter implements OnDestroy {
  @Input() products: Producto[] = [];
  @Input() currentPriceFilters: { min?: number; max?: number } = {};
  @Output() priceFiltersChanged = new EventEmitter<{
    min?: number;
    max?: number;
  }>();

  readonly chevronUpIcon = ChevronUpIcon;
  readonly chevronDownIcon = ChevronDownIcon;

  private _expanded = signal(true);
  expanded = this._expanded.asReadonly();

  private _customMinPrice = signal<number | null>(null);
  customMinPrice = this._customMinPrice.asReadonly();

  private _customMaxPrice = signal<number | null>(null);
  customMaxPrice = this._customMaxPrice.asReadonly();

  private _selectedPriceRanges = signal<Set<string>>(new Set());
  selectedPriceRanges = this._selectedPriceRanges.asReadonly();

  private _showAllPriceRanges = signal(false);
  showAllPriceRanges = this._showAllPriceRanges.asReadonly();

  private debounceTimeout: any = null;

  // Generar rangos de precios dinámicamente
  priceRanges = computed(() => {
    if (!this.products || this.products.length === 0) return [];

    const prices = this.products
      .map((p) => p.precio)
      .filter((price) => price !== null && price !== undefined && price > 0)
      .sort((a, b) => a - b);

    if (prices.length === 0) return [];

    const minPrice = prices[0];
    const maxPrice = prices[prices.length - 1];

    // Crear rangos fijos y simples
    const ranges: PriceRange[] = [
      {
        min: 0,
        max: 500,
        label: 'Menos de L. 500',
        count: this.getCountInRange(0, 500),
        selected: this.isRangeSelected(0, 500),
      },
      {
        min: 500,
        max: 1000,
        label: 'L. 500 - L. 1,000',
        count: this.getCountInRange(500, 1000),
        selected: this.isRangeSelected(500, 1000),
      },
      {
        min: 1000,
        max: 2000,
        label: 'L. 1,000 - L. 2,000',
        count: this.getCountInRange(1000, 2000),
        selected: this.isRangeSelected(1000, 2000),
      },
      {
        min: 2000,
        max: 5000,
        label: 'L. 2,000 - L. 5,000',
        count: this.getCountInRange(2000, 5000),
        selected: this.isRangeSelected(2000, 5000),
      },
      {
        min: 5000,
        max: null,
        label: 'Más de L. 5,000',
        count: this.getCountInRange(5000, null),
        selected: this.isRangeSelected(5000, null),
      },
    ];

    return ranges.filter((range) => range.count > 0);
  });

  // Rangos de precio visibles (solo los primeros 4 por defecto)
  visiblePriceRanges = computed(() => {
    const ranges = this.priceRanges();
    if (this._showAllPriceRanges() || ranges.length <= 4) {
      return ranges;
    }
    return ranges.slice(0, 4);
  });

  hasActivePriceFilters = computed(() => {
    return (
      this._selectedPriceRanges().size > 0 ||
      this._customMinPrice() !== null ||
      this._customMaxPrice() !== null
    );
  });

  private getCountInRange(min: number, max: number | null): number {
    return this.products.filter((product) => {
      const price = product.precio;
      if (price === null || price === undefined) return false;

      if (max === null) {
        return price >= min;
      }
      return price >= min && price <= max;
    }).length;
  }

  private isRangeSelected(min: number, max: number | null): boolean {
    const rangeKey = this.getRangeKey(min, max);
    return this._selectedPriceRanges().has(rangeKey);
  }

  private getRangeKey(min: number, max: number | null): string {
    return `${min}-${max}`;
  }

  toggleExpanded(): void {
    this._expanded.update((expanded) => !expanded);
  }

  toggleShowAllPriceRanges(): void {
    this._showAllPriceRanges.update((showAll) => !showAll);
  }

  private debouncedApplyCustomRange(): void {
    // Limpiar timeout anterior
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Aplicar filtro después de 300ms de inactividad
    this.debounceTimeout = setTimeout(() => {
      this.applyCustomRange();
    }, 300);
  }

  onCustomMinChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value ? parseFloat(input.value) : null;
    this._customMinPrice.set(value);

    // Limpiar rangos seleccionados cuando se usa input personalizado
    this._selectedPriceRanges.set(new Set());

    // Aplicar filtro inmediatamente
    this.applyCustomRange();
  }

  onCustomMaxChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value ? parseFloat(input.value) : null;
    this._customMaxPrice.set(value);

    // Limpiar rangos seleccionados cuando se usa input personalizado
    this._selectedPriceRanges.set(new Set());

    // Aplicar filtro inmediatamente
    this.applyCustomRange();
  }

  onCustomMinBlur(): void {
    // Aplicar filtro inmediatamente al perder foco
    this.applyCustomRange();
  }

  onCustomMaxBlur(): void {
    // Aplicar filtro inmediatamente al perder foco
    this.applyCustomRange();
  }

  applyCustomRange(): void {
    const min = this._customMinPrice();
    const max = this._customMaxPrice();

    console.log('Aplicando filtro de precio:', { min, max }); // Debug

    // Emitir el filtro
    this.priceFiltersChanged.emit({
      min: min !== null ? min : undefined,
      max: max !== null ? max : undefined,
    });
  }

  togglePriceRange(range: PriceRange): void {
    const rangeKey = this.getRangeKey(range.min, range.max);
    const currentSelected = new Set(this._selectedPriceRanges());

    if (currentSelected.has(rangeKey)) {
      currentSelected.delete(rangeKey);
    } else {
      currentSelected.add(rangeKey);
    }

    this._selectedPriceRanges.set(currentSelected);

    // Limpiar inputs personalizados cuando se usen rangos predefinidos
    this._customMinPrice.set(null);
    this._customMaxPrice.set(null);

    // Emitir filtros de precio
    this.emitPriceFilters();
  }

  private emitPriceFilters(): void {
    const selectedRanges = Array.from(this._selectedPriceRanges());
    let minPrice: number | undefined;
    let maxPrice: number | undefined;

    if (selectedRanges.length > 0) {
      const allMins: number[] = [];
      const allMaxs: number[] = [];

      selectedRanges.forEach((rangeKey) => {
        const [min, max] = rangeKey
          .split('-')
          .map((v) => (v === 'null' ? null : parseFloat(v)));
        if (min !== null) allMins.push(min);
        if (max !== null) allMaxs.push(max);
      });

      if (allMins.length > 0) minPrice = Math.min(...allMins);
      if (allMaxs.length > 0) maxPrice = Math.max(...allMaxs);
    }

    this.priceFiltersChanged.emit({ min: minPrice, max: maxPrice });
  }

  clearPriceFilters(): void {
    this._selectedPriceRanges.set(new Set());
    this._customMinPrice.set(null);
    this._customMaxPrice.set(null);
    this._showAllPriceRanges.set(false);
    this.priceFiltersChanged.emit({});
  }

  ngOnDestroy(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }
}
